import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {SearchForm, SearchFormOptionItem} from "../../../Model/Dto/SearchForm";
import {SearchFormsService} from "../../../Services/SearchForms/searchforms.service";
import {LoginService} from "../../../Services/login.service";
import {DxPopoverComponent} from "devextreme-angular";
import {TranslateService} from "@ngx-translate/core";
import {UpdateTemplateService} from "../../../Services/updateTemplate.service";
import {TemplateFieldsService} from "../../../Services/templateFields.service";
import {TemplateItemTemplate} from "../../../Model/Catalog/TemplateItemTemplate";
import {NextPimField} from "../../../Model/Catalog/NextPimField";
import {ValidFeatureService} from "../../../Services/validFeature.service";
import {ValidFeatureSystem} from "../../../Model/ValidFeatureSystem";
import DataSource from "devextreme/data/data_source";
import ArrayStore from "devextreme/data/array_store";
import {lastValueFrom} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {SystemService} from "../../../Services/system.service";

@Component({
  selector: 'np-search-forms',
  standalone: false,
  templateUrl: './search-forms.component.html',
  styleUrl: './search-forms.component.css'
})
export class SearchFormsComponent implements OnChanges {

  @Input() initialSearchForm: SearchForm;
  @ViewChild('addSearchFormPopup', {static: false}) popoverInstance: DxPopoverComponent;

  allSearchForms: SearchForm[];
  selectedSearchForm: SearchForm;
  groups: string[] = [];
  allFields: TemplateItemTemplate[] = [];
  private groupFieldsCache: Map<string, TemplateItemTemplate[]> = new Map();
  conditionValue: 'and' | 'or';
  createNewSearchFormPopoverVisible: boolean = false;
  renameSearchFormpopoverVisible: boolean = false;
  showValidationErrors: boolean = false; 

  validFeatureSystems: ValidFeatureSystem[] = [];
  featureDataSourceCache: Map<string, DataSource> = new Map();
  possibleConditions = [{
    key: 'and',
    text: this.translate.instant('und')
  }, {
    key: 'or',
    text: this.translate.instant('oder')
  }];
  
  constructor(
    public translate: TranslateService,
    public searchFormService: SearchFormsService,
    public loginService: LoginService,
    public updateTemplateService: UpdateTemplateService,
    public templateFieldsService: TemplateFieldsService,
    private validFeatureService: ValidFeatureService,
    private systemService: SystemService) {
  }

  async ngOnInit() {
    this.createNewSearchFormPopoverVisible = false;
    this.renameSearchFormpopoverVisible = false;
    this.groups = this.updateTemplateService.GetFieldGroups();
    await this.templateFieldsService.loadFields();
    this.allFields = this.templateFieldsService.translatedFields;
    this.validFeatureSystems = await lastValueFrom(
      this.validFeatureService.getSystems(this.loginService.currentUser.customerId, false)
    ) ?? [];
    await this.loadAndApplyInitialSelection();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialSearchForm'] && !changes['initialSearchForm'].firstChange) {
      this.loadAndApplyInitialSelection();
    }
  }

  private async loadAndApplyInitialSelection() {
    var allSearchForms = await this.searchFormService.getAll(this.loginService.currentUser.customerId);
    this.allSearchForms = allSearchForms ?? [];
    this.applyInitialSelection();
  }

  private async applyInitialSelection() {
    if (this.initialSearchForm && this.allSearchForms?.length) {
      this.selectedSearchForm = this.allSearchForms.find(sf => sf.id === this.initialSearchForm.id) ?? null;
    }
    if (this.selectedSearchForm?.searchOptions) {
      for (const option of this.selectedSearchForm.searchOptions) {
        if (option.nextPimField?.systemKey) {
          await this.loadFeaturesForSystem(option.nextPimField.systemKey);
        }
      }
    }
  }

  removeRow(index: number) {
    this.selectedSearchForm.searchOptions.splice(index, 1);
    if(this.isFormValid())
      this.showValidationErrors = false;
  }

  addNewRow() {
    var currentMaxOrder = this.selectedSearchForm?.searchOptions?.length ?? 0;
    this.selectedSearchForm.searchOptions.push({
      order: currentMaxOrder + 1,
      nextPimField: new NextPimField()
    });
  }

  deleteSearchForm() {
    this.searchFormService.delete(this.selectedSearchForm, this.loginService.currentUser.customerId);
    var IdToRemove = this.selectedSearchForm.id;
    this.allSearchForms = this.allSearchForms.filter(x => x.id != IdToRemove);
    this.selectedSearchForm = null;
  }

  async copySearchForm() {
    var copiedSearchForm = {...this.selectedSearchForm};
    copiedSearchForm.id = null;
    copiedSearchForm.name = copiedSearchForm.name + " " + this.translate.instant("Kopie");
    try {
      copiedSearchForm = await this.searchFormService.updateOrCreateSearchForm(copiedSearchForm, this.loginService.currentUser.customerId);
      this.allSearchForms.push(copiedSearchForm);
      this.selectedSearchForm = copiedSearchForm;
    } catch (error) {
      this.notifyLicenseError(error);
    }
  }

  async createNewSearchForm() {
    const newSearchForm = new SearchForm();
    newSearchForm.name = this.translate.instant("Neues Suchformular");
    newSearchForm.userId = this.loginService.currentUser.id;
    newSearchForm.filterCondition = this.conditionValue ?? "and";

    try {
      this.selectedSearchForm = await this.searchFormService.updateOrCreateSearchForm(newSearchForm, this.loginService.currentUser.customerId);
      this.allSearchForms.push(this.selectedSearchForm);
      this.addNewRow();
    } catch (error) {
      this.notifyLicenseError(error);
    }
  }

  private notifyLicenseError(error: unknown) {
    const key = error instanceof HttpErrorResponse && typeof error.error === 'string'
      ? error.error
      : 'Das Suchformular konnte nicht erstellt werden.';
    this.systemService.notifyWarning(this.translate.instant(key));
  }

  onReorderSearchOptions(event: any) {
    const options = this.selectedSearchForm?.searchOptions;
    if (!options) 
      return;

    const itemData = options.splice(event.fromIndex, 1)[0];
    if (itemData) {
      options.splice(event.toIndex, 0, itemData);
      options.forEach((item, index) => {
        if (item) item.order = index;
      });
    }
  }

  openNewRowPopup() {
    this.selectedSearchForm = new SearchForm();
    this.selectedSearchForm.userId = this.loginService.currentUser.id;
  }

  onSearchFormNameChanged(e: any) {
    this.selectedSearchForm.name = e.value;
    this.allSearchForms.find(x => x.id == this.selectedSearchForm.id).name = e.value;
  }

  getFieldsForGroup(group: string): TemplateItemTemplate[] {
    if (!group) return [];
    if (!this.groupFieldsCache.has(group)) {
      this.groupFieldsCache.set(group, this.allFields.filter(x => x.group === group));
    }
    return this.groupFieldsCache.get(group);
  }

  
  getFieldDisplayExpr(item: TemplateItemTemplate) {
    return item?.text === item?.field ? item?.field ?? '' : `${item.text} (${item.field})`;
  }

  private fieldToGroupCache: Map<string, string> = new Map();
  rowGroupSelection: Map<number, string> = new Map();

  getGroupForRow(row: SearchFormOptionItem): string {
    if (this.rowGroupSelection.has(row.order)) {
      return this.rowGroupSelection.get(row.order);
    }
    const fieldName = row.nextPimField?.field;
    if (!fieldName) 
      return null;
    if (!this.fieldToGroupCache.has(fieldName)) {
      const found = this.allFields.find(f => f.field === fieldName);
      this.fieldToGroupCache.set(fieldName, found?.group ?? null);
    }
    return this.fieldToGroupCache.get(fieldName);
  }

  onGroupChanged(e: any, row: SearchFormOptionItem) {
    if (!e.event) return;

    this.rowGroupSelection.set(row.order, e.value);
    if (row.nextPimField) {
      row.nextPimField.field = null;
      row.nextPimField.systemKey = null;
      row.nextPimField.elementKey = null;
    }
    row.operator = null;
    row.value = null;
    row.valueTo = null;
    row.values = null;
    this.saveSearchForm();
  }

  onFieldChanged(e: any, row: SearchFormOptionItem) {
    if (!e.event) return;
    this.rowGroupSelection.delete(row.order);
    row.operator = null;
    row.value = null;
    row.valueTo = null;
    row.values = null;
    this.saveSearchForm();
  }  

  private operatorsByDataType: Map<string, string[]> = new Map([
    ['string', ['equals', 'equals_not', 'contains', 'contains_not', 'begins_with', 'ends_with', 'is_empty', 'is_not_empty']],
    ['number', ['equals', 'equals_not', 'greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'not_between', 'is_empty', 'is_not_empty']],
    ['date', ['at', 'before', 'after', 'between', 'is_empty', 'is_not_empty']],
    ['boolean', ['is_true', 'is_false']],
    ['enum', ['equals', 'equals_not', 'is_empty', 'is_not_empty']],
    ['multiEnum', ['is_in', 'is_not_in', 'contains_all', 'contains_any', 'is_empty', 'is_not_empty']],
  ]);

  private noValueOperators = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);
  private rangeOperators = new Set(['between', 'not_between']);

  getOperatorsForField(fieldName: string, group: string): string[] {
    if (!fieldName || !group) return [];
    const dataType = this.getDataTypeForField(fieldName, group);
    return this.operatorsByDataType.get(dataType) ?? this.operatorsByDataType.get('string');
  }

  translateGroup = (item: string): string => {
    return item ? this.translate.instant(item) : '';
  }

  translateOperator = (item: string): string => {
    return item ? this.translate.instant(item) : '';
  }

  getDataTypeForField(fieldName: string, group: string): string {
    if (!fieldName || !group) return 'string';
    const fields = this.getFieldsForGroup(group);
    const field = fields.find(f => f.field === fieldName);
    return field?.dataType ?? 'string';
  }

  getDataTypeForRow(row: SearchFormOptionItem): string {
    return this.getDataTypeForField(row.nextPimField?.field, this.getGroupForRow(row));
  }

  isNoValueOperator(operator: string): boolean {
    return this.noValueOperators.has(operator);
  }

  isRangeOperator(operator: string): boolean {
    return this.rangeOperators.has(operator);
  }

  onOperatorChanged(e: any, row: SearchFormOptionItem) {
    if (!e.event) 
      return;
    const newOp = e.value;
    if (this.isNoValueOperator(newOp)) {
      row.value = null;
      row.valueTo = null;
      row.values = null;
    } else if (this.isRangeOperator(newOp)) {
      row.values = null;
    } else {
      row.valueTo = null;
    }
    this.saveSearchForm();
  }

  isFormValid(): boolean {
    if (!this.selectedSearchForm?.searchOptions?.length) 
      return true;
    return this.selectedSearchForm.searchOptions.every(row => this.isRowValid(row));
  }

  isRowValid(row: SearchFormOptionItem): boolean {
    if (!row.nextPimField?.field || !row.operator) 
      return false;
    if (this.isNoValueOperator(row.operator)) 
      return true;
  
    return true;
  }

  isFeatureGroup(row: SearchFormOptionItem): boolean {
    return this.getGroupForRow(row) === 'Features';
  }

  needsSystem(row: SearchFormOptionItem): boolean {
    if (!row.nextPimField?.field || !this.isFeatureGroup(row)) return false;
    const fields = this.getFieldsForGroup('Features');
    const field = fields.find(f => f.field === row.nextPimField.field);
    return field?.system === true;
  }

  needsElement(row: SearchFormOptionItem): boolean {
    if (!row.nextPimField?.field || !this.isFeatureGroup(row)) return false;
    const fields = this.getFieldsForGroup('Features');
    const field = fields.find(f => f.field === row.nextPimField.field);
    return field?.element === true;
  }

  async onFeatureSystemChanged(row: SearchFormOptionItem) {
    row.nextPimField.elementKey = null;
    await this.loadFeaturesForSystem(row.nextPimField.systemKey);
    this.saveSearchForm();
  }

  async loadFeaturesForSystem(systemId: string) {
    if (!systemId || this.featureDataSourceCache.has(systemId)) 
      return;
    const features = await lastValueFrom(
      this.validFeatureService.getFeatures(systemId, this.loginService.currentUser.customerId, false)
    ) ?? [];
    this.featureDataSourceCache.set(systemId, new DataSource({
      store: new ArrayStore({ key: 'id', data: features }),
      paginate: true,
      pageSize: 20
    }));
  }

  getFeaturesDataSource(systemId: string): DataSource {
    return this.featureDataSourceCache.get(systemId) ?? null;
  }

  featureDisplayExpr = (item: any): string => {
    if (!item) return '';
    return item.unit ? `${item.name} => ${item.unit}` : item.name;
  };

  async saveSearchForm(): Promise<boolean> {
    if (!this.isFormValid())
      return false;
    if (this.selectedSearchForm?.searchOptions) {
      for (const row of this.selectedSearchForm.searchOptions) {
        if (row.value != null && typeof row.value !== 'string') {
          row.value = row.value instanceof Date
            ? row.value.toISOString()
            : String(row.value);
        }
        if (row.valueTo != null && typeof row.valueTo !== 'string') {
          row.valueTo = row.valueTo instanceof Date
            ? row.valueTo.toISOString()
            : String(row.valueTo);
        }
      }
    }
    try {
      const saved = await this.searchFormService.updateOrCreateSearchForm(this.selectedSearchForm, this.loginService.currentUser.customerId);
      this.selectedSearchForm.id = saved.id;
    } catch (error) {
      this.notifyLicenseError(error);
      return false;
    }
    this.showValidationErrors = false;
    return true;
  }

  getValidationErrors(): string[] {
    if (!this.selectedSearchForm?.searchOptions?.length) 
      return [];
    const errors: string[] = [];
    this.selectedSearchForm.searchOptions.forEach((row, i) => {
      if (!row.nextPimField?.field) {
        errors.push(this.translate.instant('Zeile') + ` ${i + 1}: ` + this.translate.instant('Kein Feld ausgewählt'));
      } else if (!row.operator) {
        errors.push(this.translate.instant('Zeile') + ` ${i + 1}: ` + this.translate.instant('Kein Operator ausgewählt'));
      } else if (!this.isNoValueOperator(row.operator)) {
        if (this.isRangeOperator(row.operator)) {
          if (row.value == null || row.value === '' || row.valueTo == null || row.valueTo === '') {
            errors.push(this.translate.instant('Zeile') + ` ${i + 1}: ` + this.translate.instant('Von- und Bis-Wert erforderlich'));
          }
        } 
      }
    });
    return errors;
  }

}
